import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, Table, Button, Modal, Select, message, Tag, Popconfirm, Typography, Space } from 'antd';
import { PlusOutlined, UserDeleteOutlined, CrownOutlined } from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';

const { Text } = Typography;

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const AdminManagePanel = () => {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [picked, setPicked] = useState(null);
  const [saving, setSaving] = useState(false);
  const candQRef = useRef('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get('/admin/admins', { headers: authHeaders() });
      if (res.data.success) setAdmins(res.data.admins || []);
    } catch (e) {
      message.error('获取管理员列表失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // 防竞态：只采用最新一次输入的结果，空输入不返回全部
  const searchCandidates = async (q) => {
    const query = (q || '').trim();
    candQRef.current = query;
    if (!query) { setOptions([]); return; }
    try {
      const res = await axios.get('/admin/admins/candidates', { headers: authHeaders(), params: { q: query } });
      if (candQRef.current !== query) return; // 丢弃过期结果
      if (res.data.success) {
        setOptions((res.data.users || []).map((u) => ({
          value: u.id,
          label: `${u.nickname || '未命名'}${u.phone ? ` (${u.phone})` : ''}${u.member_level ? ` · ${u.member_level}` : ''}`,
        })));
      }
    } catch (_) { /* ignore */ }
  };

  const openAdd = () => {
    setPicked(null);
    setOptions([]);
    setAddOpen(true);
  };

  const doAdd = async () => {
    if (!picked) { message.warning('请选择一个会员'); return; }
    setSaving(true);
    try {
      const res = await axios.post('/admin/admins', { userId: picked }, { headers: authHeaders() });
      if (res.data.success) {
        message.success('已设为管理员');
        setAddOpen(false);
        load();
      } else {
        message.error(res.data.message || '设置失败');
      }
    } catch (e) {
      message.error(e.response?.data?.message || '设置失败');
    } finally {
      setSaving(false);
    }
  };

  const doRemove = async (id) => {
    try {
      const res = await axios.delete(`/admin/admins/${id}`, { headers: authHeaders() });
      if (res.data.success) {
        message.success('已撤销管理员');
        load();
      } else {
        message.error(res.data.message || '撤销失败');
      }
    } catch (e) {
      message.error(e.response?.data?.message || '撤销失败');
    }
  };

  const columns = [
    { title: '昵称', dataIndex: 'nickname', render: (v) => v || '未命名' },
    { title: '手机号', dataIndex: 'phone', render: (v) => v || '-' },
    { title: '会员级别', dataIndex: 'member_level', render: (v) => (v ? <Tag color="gold">{v}</Tag> : '-') },
    { title: '成为管理员', dataIndex: 'created_at', width: 160, render: (v) => (v ? dayjs(v).format('YYYY-MM-DD') : '-') },
    {
      title: '操作',
      width: 140,
      render: (_, r) => (
        <Popconfirm title={`撤销「${r.nickname || r.phone}」的管理员权限？`} onConfirm={() => doRemove(r.id)} okText="撤销" cancelText="取消">
          <Button size="small" danger icon={<UserDeleteOutlined />}>撤销管理员</Button>
        </Popconfirm>
      ),
    },
  ];

  return (
    <Card
      title={<span><CrownOutlined style={{ color: '#faad14', marginRight: 6 }} />管理员管理</span>}
      extra={<Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>新增管理员</Button>}
    >
      <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
        从已有会员中设置管理员。被设为管理员后，该用户在「我的」页会出现「管理后台」入口，可管理本系统。
      </Text>
      <Table rowKey="id" size="small" loading={loading} dataSource={admins} columns={columns}
        pagination={{ pageSize: 10, hideOnSinglePage: true }} scroll={{ x: 560 }} />

      <Modal title="新增管理员" open={addOpen} onCancel={() => setAddOpen(false)} onOk={doAdd} confirmLoading={saving} okText="设为管理员">
        <div style={{ marginBottom: 8, color: '#8c8c8c', fontSize: 13 }}>搜索并选择一个会员，将其设为管理员：</div>
        <Select
          showSearch
          allowClear
          placeholder="输入昵称或手机号搜索"
          style={{ width: '100%' }}
          filterOption={false}
          onSearch={searchCandidates}
          onChange={(v) => setPicked(v)}
          value={picked}
          options={options}
          notFoundContent="无匹配会员"
        />
      </Modal>
    </Card>
  );
};

export default AdminManagePanel;
